import { Landmark } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { AssistantCard, AssistantCardBody, AssistantCardHeader } from "../theme";

export interface BankAccount {
  name: string;
  last4: string;
}

export interface BankConnection {
  connection_id: number;
  institution: string;
  accounts: BankAccount[];
  status: "active" | "revoked";
  status_label: string;
  connected_on: string;
  login_expired_on: string | null;
}

const isText = (value: unknown): value is string => typeof value === "string";
const isDate = (value: unknown): value is string =>
  isText(value) && /^\d{4}-\d{2}-\d{2}$/.test(value);

function isAccount(value: unknown): value is BankAccount {
  const account = value as Partial<BankAccount> | null;
  return !!account && isText(account.name) && isText(account.last4);
}

function isConnection(value: unknown): value is BankConnection {
  const row = value as Partial<BankConnection> | null;
  return (
    !!row &&
    typeof row.connection_id === "number" &&
    isText(row.institution) &&
    Array.isArray(row.accounts) &&
    row.accounts.every(isAccount) &&
    (row.status === "active" || row.status === "revoked") &&
    isText(row.status_label) &&
    isDate(row.connected_on) &&
    (row.login_expired_on === null || isDate(row.login_expired_on))
  );
}

/** list_bank_connections' result as the card shows it, or null when it isn't that shape. */
export function bankConnections(result: unknown): BankConnection[] | null {
  const connections = (result as { connections?: unknown } | null)?.connections;
  return Array.isArray(connections) && connections.every(isConnection) ? connections : null;
}

/** "2026-09-02" as "Sep 2, 2026": a calendar date, so no time zone may move it. */
export function bankDate(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * The second line of a row: its accounts, then when its login expired, or when it connected if it
 * still is (a removed one says neither).
 */
export function connectionDetails(connection: BankConnection): string {
  const accounts = connection.accounts.map((account) =>
    account.last4 ? `${account.name} ••${account.last4}` : account.name
  );
  const when = connection.login_expired_on
    ? `Login expired ${bankDate(connection.login_expired_on)}`
    : connection.status === "active"
      ? `Connected ${bankDate(connection.connected_on)}`
      : null;
  return [...accounts, ...(when ? [when] : [])].join(" · ");
}

/**
 * The application's bank connections, read by the assistant (design: "Bank connections", inline).
 * Rows carry no actions in this build: revoking an Active one is a design todo.
 */
export function BankConnectionsCard({ connections }: { connections: BankConnection[] }) {
  return (
    <AssistantCard>
      <AssistantCardHeader icon={Landmark} title="Bank connections" />
      <AssistantCardBody className="pt-0">
        {connections.length === 0 ? (
          <p className="py-2 text-muted-foreground">No bank connected</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {connections.map((connection) => (
              <li key={connection.connection_id} className="py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 break-words font-medium">{connection.institution}</span>
                  <Badge
                    className={cn(
                      "border-transparent shadow-none",
                      connection.status === "active"
                        ? "bg-success-soft text-success"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {connection.status_label}
                  </Badge>
                </div>
                <p className="mt-0.5 break-words text-xs text-muted-foreground">
                  {connectionDetails(connection)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </AssistantCardBody>
    </AssistantCard>
  );
}
